import styles from './FactBox.module.scss';
import Card from '@ui/components/Card/Card';
import Heading from '@ui/components/Heading/Heading';
import List, { ListItem } from '@ui/components/List/List';
import FontAwesomeProIcon from '@ui/components/FontAwesomeProIcon/FontAwesomeProIcon';
import Content from '@ui/components/Content/Content';
import SliderControl from '@ui/components/Slider/SliderControls';
import CardImage from '@ui/components/Card/components/CardImage/CardImage';
import Slider from '@ui/components/Slider/Slider';
import CardBody from '@ui/components/Card/components/CardBody/CardBody';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { nanoid } from 'nanoid';
import { getHeadingTag } from '../../utils';

const FactBox = ({ headingLevel = 3, slides, defaultSettings }) => {
  const [elements, setElements] = useState([]);
  const sliderSettings = {
    slidesPerView: 1,
    spaceBetween: 20,
    breakpoints: {
      576: {
        slidesPerView: 2,
      },
    },
    ...(defaultSettings || {}),
  };

  useEffect(() => {
    setElements(slides?.map(slide => ({ id: nanoid(), ...slide })));
  }, []);

  return (
    <div className={styles.container}>
      <Slider bottomNavigation isFullWidth={false} {...sliderSettings}>
        <div className={styles.controlsContainer} data-no-slide="true">
          <SliderControl className={styles.controlButton} direction="left" />
          <SliderControl className={styles.controlButton} direction="right" />
        </div>
        {elements.map(slide => (
          <Card className={styles.cardContainer} key={slide?.id}>
            {!!slide?.image && (
              <CardImage
                aspectRatio={0.6}
                className={{
                  [styles.fullSizeImage]: !slide?.title,
                }}
                image={slide?.image}
              />
            )}
            {(slide?.icon || slide?.title || slide?.content || slide?.list) && (
              <CardBody>
                {!!slide?.icon && (
                  <FontAwesomeProIcon
                    className={classNames(styles.icon, {
                      [styles.iconLarge]: !slide?.content,
                    })}
                    icon={slide?.icon}
                  />
                )}
                {!!slide?.title && (
                  <Heading
                    as={getHeadingTag(headingLevel)}
                    size={slide?.content ? 'h5' : 'h2'}
                  >
                    {slide?.title}
                  </Heading>
                )}
                {!!slide?.content && (
                  <Content noSpacing withParser>
                    {slide?.content}
                  </Content>
                )}
                {!!slide?.list && (
                  <List
                    className={styles.list}
                    icon={<FontAwesomeProIcon icon={slide?.list?.icon} />}
                    isDense
                    type={slide?.list?.type}
                    variant={slide?.list?.variant}
                  >
                    {slide?.list?.items?.map(({ id, text }) => (
                      <ListItem key={id}>{text}</ListItem>
                    ))}
                  </List>
                )}
              </CardBody>
            )}
          </Card>
        ))}
      </Slider>
    </div>
  );
};

FactBox.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  defaultSettings: PropTypes.objectOf(PropTypes.any),
  hasBackground: PropTypes.bool,
  headingLevel: PropTypes.number,
  slides: PropTypes.arrayOf(
    PropTypes.shape({
      image: PropTypes.shape({
        src: PropTypes.string,
        srcSet: PropTypes.string,
        alt: PropTypes.string,
      }),
      icon: PropTypes.string,
      content: PropTypes.string,
      list: PropTypes.shape({
        type: PropTypes.string,
        icon: PropTypes.string,
        items: PropTypes.arrayOf(
          PropTypes.shape({
            text: PropTypes.string,
            id: PropTypes.string,
          }),
        ),
      }),
    }),
  ).isRequired,
};

export default FactBox;
